/**
 * @file BackupWebhookSection.tsx
 * @description «Ключ доступа агента» (phase19.26 шаг 7; шаг 7.1 — только маска,
 * экстренный отзыв).
 *
 * Агент — программа на сервере приложения, которая делает копии, — подписывает
 * этим ключом каждый отчёт панели. Полный ключ панель не получает (Р3):
 * показываем маску, дату создания и состояние замены. Две операции:
 * - плановая замена (`BackupTokenRotateDialog`): новый ключ ждёт агента, тот
 *   забирает его при следующем запуске и подтверждает; не подтвердил за 48 ч —
 *   предупреждение (`pendingStale`). Доступна только при загруженных сведениях:
 *   замена не срочная, а без них не видно, не ждёт ли агента уже другой ключ;
 * - экстренный отзыв (`BackupTokenRevokeDialog`, Р2): старый ключ гаснет сразу,
 *   панель перестаёт видеть агента до повторной установки — страница узнаёт об
 *   этом через `onRevoked` и убирает ранее показанную команду установки. Отзыв —
 *   действие на случай утечки, поэтому доступен, даже если сведения о ключе не
 *   загрузились.
 *
 * Блок не зависит от публичного адреса API: `url === null` — маска и обе
 * операции работают, вместо адреса отчётов показываем, что команду установки
 * получить нельзя.
 */

import { KeyRound, RefreshCw, RotateCcw, ShieldOff } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { EditorialCard } from "../../ui/EditorialCard";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import {
  backupErrorMessage,
  getBackupWebhookInfo,
  type BackupWebhookInfoDto,
} from "../../../lib/api-backups";
import { BackupTokenRevokeDialog } from "./BackupTokenRevokeDialog";
import { BackupTokenRotateDialog } from "./BackupTokenRotateDialog";

interface Props {
  /** Ключ экстренно отозван — панель не видит агента до повторной установки. */
  onRevoked: () => void;
}

/** Предупреждение вместо адреса отчётов, когда у сервера нет публичного адреса. */
const NO_PUBLIC_URL_TEXT =
  "У сервера не задан публичный адрес — команду установки получить нельзя, пока администратор платформы его не настроит.";

const since = (iso: string) => new Date(iso).toLocaleString("ru-RU");

export function BackupWebhookSection({ onRevoked }: Props) {
  const notify = useNotify();
  const [info, setInfo] = useState<BackupWebhookInfoDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [dialog, setDialog] = useState<"rotate" | "revoke" | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInfo(await getBackupWebhookInfo());
      setFailed(false);
    } catch (err) {
      setFailed(true);
      notify.error(backupErrorMessage(err, "Не удалось получить сведения о ключе агента"));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  // Маска не секрет — читаем сразу, без кнопки «Показать».
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Ключ доступа агента"
        subtitle="Агент — программа на сервере приложения, которая делает копии; этим ключом он подтверждает свои отчёты"
      />
      <EditorialCard.Body>
        <div className="flex flex-col gap-4">
          {!info ? (
            <p className="text-sm text-on-surface-variant">
              {loading
                ? "Загрузка…"
                : failed
                  ? "Сведения о ключе не загрузились — нажмите «Обновить». Экстренный отзыв доступен и без них."
                  : "Сведения о ключе не загружены."}
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-1 rounded-xl bg-surface-low px-3 py-2.5">
                <span className="flex flex-wrap items-center gap-x-2 text-sm text-on-surface">
                  <KeyRound className="size-4 shrink-0 text-on-surface-variant" aria-hidden />
                  Ключ доступа агента:
                  <span className="font-mono">{info.tokenMask ?? "ещё не создан"}</span>
                </span>
                {info.createdAt ? (
                  <span className="text-xs text-on-surface-variant">Создан: {since(info.createdAt)}</span>
                ) : null}
                {info.url ? (
                  <span className="break-all text-xs text-on-surface-variant">
                    Агент отправляет отчёты на: {info.url}
                  </span>
                ) : null}
              </div>

              {info.url === null ? <NoticeStrip tone="warning">{NO_PUBLIC_URL_TEXT}</NoticeStrip> : null}

              {info.pending && info.pendingSince ? (
                info.pendingStale ? (
                  <NoticeStrip tone="warning">
                    Агент не подтвердил новый ключ за 48 ч — проверьте, что бэкап на сервере
                    приложения запускается. Новый ключ ждёт с {since(info.pendingSince)}.
                  </NoticeStrip>
                ) : (
                  <Badge variant="warning" className="self-start whitespace-normal py-1">
                    Новый ключ ждёт подтверждения агентом с {since(info.pendingSince)}
                  </Badge>
                )
              ) : null}
            </>
          )}

          {/* Один порядок на всех ширинах: от безобидного к опасному, опасное — последним. */}
          <div className="flex flex-col gap-2 lg:flex-row lg:justify-end">
            <Button variant="ghost" onClick={() => void load()} loading={loading}>
              <RefreshCw className="size-4" />
              Обновить
            </Button>
            <Button
              // Замена ключа — с последствиями, но обратимая: мягкая янтарная.
              variant="warning"
              onClick={() => setDialog("rotate")}
              disabled={!info}
              title={!info ? "Сначала загрузите сведения о ключе" : undefined}
            >
              <RotateCcw className="size-4" />
              Плановая замена
            </Button>
            {/* Отзыв — на случай утечки: не ждёт успешной загрузки сведений.
                На странице — мягкая красная; сплошная — в окне подтверждения. */}
            <Button variant="destructive" onClick={() => setDialog("revoke")}>
              <ShieldOff className="size-4" />
              Экстренный отзыв
            </Button>
          </div>
        </div>
      </EditorialCard.Body>

      <BackupTokenRotateDialog
        open={dialog === "rotate"}
        onClose={() => setDialog(null)}
        onRotated={() => void load()}
      />
      <BackupTokenRevokeDialog
        open={dialog === "revoke"}
        onClose={() => setDialog(null)}
        onRevoked={(revoked) => {
          // Сведения — из ответа отзыва: не зависят от того, загрузились ли они раньше.
          setInfo(revoked);
          setFailed(false);
          onRevoked();
        }}
      />
    </EditorialCard>
  );
}
